import { OpenBankingResultStatus } from "consts/transactions";
import { ApiState } from "infrastructure/api";
import { RootState } from "infrastructure/store";
import type { Option } from "types";
import { capitalizeFirstLetterFormatter, removeUnderscoreFormatter } from "util/formaters";
import { compareIds } from "util/support";
import { convertToOptions } from "util/transformers";
import type { Country, Ledger } from "./types";

export const selectMenuOpenState = (state: RootState) =>
  state.general.isSideMenuOpen;

export const selectNotification = (state: RootState) =>
  state.general.notifications;

export const selectConstants = (state: RootState) =>
  state.general.constants;

export const selectIsBootstrapDataLoading = (state: RootState) =>
  state.general.constantsLoadingState === ApiState.Pending;

export const selectIsBootstrapDataSucceeded = (state: RootState) =>
  state.general.constantsLoadingState === ApiState.Succeeded;

export const selectCountries = (sortFn = (countryA: Country, countryB: Country) => (
  countryA.shortName.localeCompare(countryB.shortName)
)) => (state: RootState) =>
    [state.general.countries]
      .flat()
      .filter(Boolean)
      .sort(sortFn);

export const selectCurrencies = (state: RootState) =>
  selectConstants(state).Currencies;

export const selectSettings = (state: RootState) =>
  state.general.settings;

export const selectPayoutCurrency = (state: RootState) =>
  selectSettings(state)?.payoutCurrency ?? 'EUR';

export const selectDomains = (state: RootState) =>
  state.general.domains;

export const selectIsCountriesLoading = (state: RootState) =>
  state.general.countriesLoadingState === ApiState.Pending;

export const selectDomainsOptions = (state: RootState) =>
  Object.keys(state.general.domains?.setupValue ?? {});

export const selectBootstrapData = (state: RootState) => ({
  ...selectConstants(state),
  Domains: selectDomainsOptions(state)
});

export const selectOnboardingLegalFormOptions = (state: RootState) =>
  convertToOptions(
    selectConstants(state).OnboardingLegalForms
  );

export const selectOnboardingLegalForms = (state: RootState) =>
  selectConstants(state).OnboardingLegalForms ?? [];

export const selectMerchantPayoutIncrementsVia = (state: RootState) =>
  convertToOptions(
    selectConstants(state).MerchantPayoutIncrementsVia,
    capitalizeFirstLetterFormatter
  )

export const selectViaAutoSuggestModel = (state: RootState) =>
  selectConstants(state).Via.map(({ name }: Option) => ({
    id: name
      .charAt(0)
      .toLocaleLowerCase(),
    name
  }));

export const selectCalulationStatus = (state: RootState) =>
  selectConstants(state).MerchantPayoutCalculationEnums.map((calculationStatus: string) => ({
    id: calculationStatus,
    name: removeUnderscoreFormatter(calculationStatus)
  }));

export const selectTransactionCurrencies = (state: RootState) =>
  selectConstants(state).TransactionCurrencies;

export const selectFileTaxName = (state: RootState) =>
  selectConstants(state).MerchantPayoutFileTaxName;

export const selectParentVisibility = (state: RootState) =>
  state.general.extras.parentVisibility;

export const selectObResultStatusText = (obResultStatus: OpenBankingResultStatus) =>
  (state: RootState) =>
    selectConstants(state).ObResultStatuses.find(({ id }: Option) =>
      compareIds(id, obResultStatus))?.name ?? '';

export const selectCountriesByProperty = <TProperty extends keyof Country>(
  propertyName: TProperty,
  values: Array<Country[TProperty]>
) => (state: RootState) =>
    selectCountries()(state)
      .filter((country: Country) => values.includes(country[propertyName]));

export const selectUserPreferences = (state: RootState) =>
  state.general.userPreferences;

export const selectUserPreferencesLoadingState = (state: RootState) =>
  state.general.userPreferencesLoadingState;

export const selectKycTypesMap = (state: RootState) =>
  selectConstants(state).KycTypesMap;

export const selectIsLedgersLoading = (state: RootState) =>
  state.general.ledgersLoadingState === ApiState.Pending;

export const selectLedgerCurrencies = (state: RootState) =>
  state.general.ledgers
    ?.data.map(({ currency }: Ledger) => currency) ?? [];

export const selectECommerceShoppingPlatforms = (state: RootState) =>
    selectConstants(state).ECommerceShoppingPlatforms ?? [];
